import { getPrivacyFriendlyFingerprint } from "./fingerprint/dailyFingerprint";
import { Collector } from "./collector/collector";
import { Config } from "./collector/config";
import { Helper } from "./collector/helper";
import { Url } from "./collector/url";
import { Cookie } from "./collector/cookie";
import { isbot } from 'isbot';

/**
 * BonsaitAnalytics - Handles site usage statistics collection
 * 
 * @example
 * // Initialize analytics and track pageview
 * BonsaitAnalytics.setup();
 * 
 * @example
 * // Track custom event
 * BonsaitAnalytics.trackEvent('button_click', { button: 'signup' });
 * 
 * @example
 * // Track pageview for external site
 * BonsaitAnalytics.trackPageview('external-site-id');
 */
export class BonsaitAnalytics {
    
    /**
     * Initialize analytics tracking system
     * Sets up event listeners and tracks initial pageview
     */
    static setup() {
        this._setupEventListeners();
        this.trackPageview();
    }

    /**
     * Track a pageview event
     * @param {string} [siteId] - Optional external site ID for cross-site tracking
     */
    static trackPageview(siteId = null) {
        if (siteId) {
            Config.id = siteId;
            Config.external = true;
        }
        this._getBrowserFingerprint();
    }

    /**
     * Track a custom event
     * @param {string} eventName - Name of the event to track
     * @param {Object} [eventData] - Optional data to attach to the event
     * @param {string} [siteId] - Optional external site ID for cross-site tracking
     */
    static trackEvent(eventName, eventData = null, siteId = null) {
        if (!eventName) {
            console.warn('BonsaitAnalytics.trackEvent: eventName is required');
            return false;
        }

        if (siteId) {
            Config.id = siteId;
            Config.external = true;
        }

        this._sendEvent(eventName, eventData);
        return true;
    }

    /**
     * Add a custom parameter to all future tracking events
     * @param {string} key - Parameter name
     * @param {*} value - Parameter value
     */
    static addParameter(key, value) {
        Config.params[key] = () => value;
    }

    // ===== Private Methods =====

    /**
     * Get privacy-friendly fingerprint (changes daily) and trigger initial pageload event
     * @private
     */
    static _getBrowserFingerprint() {
        Config.external = true;
        getPrivacyFriendlyFingerprint()
            .then((fingerprint) => {
                Config.fingerprint = fingerprint;
                this._sendEvent('pageload');
                
                // Check for event in URL params
                const urlEvent = Url.getParameterByName('bonsait_collect');
                if (urlEvent) {
                    this._sendEvent(urlEvent);
                }
            })
            .catch((err) => {
                console.error('BonsaitAnalytics: Failed to get fingerprint', err);
            });
    }

    /**
     * Send an event to the collector
     * @private
     * @param {string} eventName - Event name
     * @param {*} [eventData] - Optional event data
     */
    static _sendEvent(eventName, eventData = null) {
        Config.external = false;

        // Respect Do Not Track header
        if (navigator.doNotTrack === "1" || navigator.doNotTrack === "yes") {
            return;
        }

        // Respect user opt-out preference
        try {
            if (localStorage.getItem('bonsait_analytics_optout') === 'true') {
                return;
            }
        } catch (e) {
            // localStorage might be blocked, continue
        }

        // Skip tracking for bots
        if (isbot(navigator.userAgent)) {
            return;
        }

        // Handle special pageload/pageclose events
        if (eventName === 'pageload' && Config.pageLoadOnce) {
            return;
        }
        if (eventName === 'pageload') {
            Config.pageLoadOnce = true;
        }
        if (eventName === 'pageclose') {
            return; // Pageclose is handled by event listener
        }

        new Collector(eventName, Helper.now(), eventData);
    }

    /**
     * Setup automatic event listeners for page lifecycle and clicks
     * @private
     */
    static _setupEventListeners() {
        // Delete old uid cookie (moving to cookieless tracking)
        document.cookie = "__BonsaitCollector_uid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        
        // Save UTM parameters
        Cookie.setUtms();

        // Setup pageclose tracking
        this._setupPageCloseTracking();
        
        // Setup click tracking
        this._setupClickTracking();
    }

    /**
     * Setup page close/unload tracking
     * @private
     */
    static _setupPageCloseTracking() {
        // Safari does not reliably fire the `pagehide` or `visibilitychange`
        const isSafari = typeof safari === 'object' && safari.pushNotification;
        const isPageHideSupported = 'onpageshow' in self;
        
        // IE9-10 do not support the pagehide event, so we fall back to unload
        const pageCloseEvent = isPageHideSupported && !isSafari ? 'pagehide' : 'unload';

        window.addEventListener(pageCloseEvent, () => {
            if (!Config.pageCloseOnce) {
                Config.pageCloseOnce = true;
                
                // Include external link in event data if clicked recently
                const eventData = () => {
                    if (Helper.isPresent(Config.externalHost) && 
                        (Helper.now() - Config.externalHost.time) < 5000) {
                        return Config.externalHost.link;
                    }
                };
                
                new Collector('pageclose', Helper.now(), eventData);
            }
        });
    }

    /**
     * Setup automatic click tracking for links and custom attributes
     * @private
     */
    static _setupClickTracking() {
        window.addEventListener('load', () => {
            // Track external link clicks
            const links = document.getElementsByTagName('a');
            for (let i = 0; i < links.length; i++) {
                links[i].addEventListener('click', function() {
                    if (Url.externalHost(this)) {
                        Config.externalHost = { 
                            link: this.href, 
                            time: Helper.now() 
                        };
                    }
                });
            }

            // Track elements with data-BonsaitCollector-event attribute
            const trackedElements = document.querySelectorAll('[data-BonsaitCollector-event]');
            for (let i = 0; i < trackedElements.length; i++) {
                trackedElements[i].addEventListener('click', function() {
                    const eventName = this.getAttribute('data-BonsaitCollector-event');
                    if (eventName) {
                        BonsaitAnalytics.trackEvent(eventName, eventName);
                    }
                });
            }
        });
    }
}
